// The assistant conversation.
//
// In the store rather than in AiPanel because the panel unmounts constantly:
// every time it is closed, and every time "Take me there" navigates somewhere.
// Component state meant the customer lost the thread the moment they acted on
// an answer, which is exactly when they were most likely to have a follow-up.
//
// Deliberately NOT persisted — see the blacklist in store.ts. The transcript
// holds balances, amounts and due dates, and writing those to localStorage
// would leave them on the machine after the tab is closed and after the
// customer has logged out. Living in memory means the conversation survives
// navigation, which is what was asked for, and nothing more.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useAppSelector } from "store/hooks";
// Both `import type` deliberately. aiApi reaches axiosApi, which imports the
// store, which imports this slice — a value import here would close that loop
// at module-eval time. `import type` is erased, so the cycle never exists at
// runtime.
import type { AiAction } from "components/ai/aiApi";
import type { RootState } from "store/store";

export interface AiMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  /** Set on an assistant turn that offered a screen to open. */
  action?: AiAction;
  failed?: boolean;
}

/**
 * Which face of the panel is showing: the chat, or the help-guide index.
 * In the store rather than in AiPanel so an entry point elsewhere (a "Help"
 * menu item, say) can open the panel already on the guides.
 */
export type AiMode = "ask" | "browse";

interface AiState {
  /**
   * Whether the AI service accepted this customer's token. Checked once by
   * AiPanelHost and read by both launchers, so the desktop header and the
   * mobile navbar cannot disagree about whether to show the button — and so
   * the check is not made twice on every page.
   */
  available: boolean;
  open: boolean;
  mode: AiMode;
  messages: AiMessage[];
  /** True while an answer is streaming, so the composer stays disabled. */
  busy: boolean;
  /** Monotonic, so ids stay unique across "New chat". */
  nextId: number;
}

const initialState: AiState = {
  available: false,
  open: false,
  mode: "ask",
  messages: [],
  busy: false,
  nextId: 1,
};

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    setAiAvailable: (state, action: PayloadAction<boolean>) => {
      state.available = action.payload;
      // A session that stops being accepted mid-visit — the portal token lasts
      // 30 minutes — must not leave an open panel that silently 403s.
      if (!action.payload) state.open = false;
    },

    setAiOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },

    setAiMode: (state, action: PayloadAction<AiMode>) => {
      state.mode = action.payload;
    },

    /**
     * Start a turn: the question and the empty answer it will stream into.
     * Both ids are returned to the caller via the reducer's own counter rather
     * than generated in the component, so two launchers (desktop and mobile)
     * can never mint the same one.
     */
    startAiTurn: (state, action: PayloadAction<string>) => {
      const userId = state.nextId++;
      const answerId = state.nextId++;
      state.messages.push({ id: userId, role: "user", content: action.payload });
      state.messages.push({ id: answerId, role: "assistant", content: "" });
      state.busy = true;
    },

    appendAiToken: (
      state,
      action: PayloadAction<{ id: number; text: string }>
    ) => {
      const message = state.messages.find((item) => item.id === action.payload.id);
      if (message) message.content += action.payload.text;
    },

    patchAiMessage: (
      state,
      action: PayloadAction<{ id: number } & Partial<AiMessage>>
    ) => {
      const { id, ...change } = action.payload;
      const index = state.messages.findIndex((item) => item.id === id);
      if (index !== -1) state.messages[index] = { ...state.messages[index], ...change };
    },

    setAiBusy: (state, action: PayloadAction<boolean>) => {
      state.busy = action.payload;
    },

    /** New chat. Keeps the panel open — the customer asked for a blank slate,
     *  not for the panel to disappear. */
    clearAiConversation: (state) => {
      state.messages = [];
      state.busy = false;
    },

    /** Logout, and any other point where this customer's session ends. */
    clearAiSlice: () => initialState,
  },
});

export const {
  setAiAvailable,
  setAiOpen,
  setAiMode,
  startAiTurn,
  appendAiToken,
  patchAiMessage,
  setAiBusy,
  clearAiConversation,
  clearAiSlice,
} = aiSlice.actions;

export default aiSlice.reducer;

const selectAi = (state: RootState) => state.ai;
export const useAiState = () => {
  const ai = useAppSelector(selectAi);
  return useMemo(() => ai, [ai]);
};
